import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plug, CheckCircle2, Settings2, ChevronDown, Plus, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const STATUS_STYLES = {
  Connected: "bg-success/15 text-success",
  Available: "bg-gold/15 text-gold-dark",
  Planned: "bg-secondary text-charcoal/50",
};

const ConnectorCard = ({ c, onSaved, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(c.enabled);
  const [baseUrl, setBaseUrl] = useState(c.config?.base_url || "");
  const [apiKey, setApiKey] = useState("");
  const [notes, setNotes] = useState(c.config?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/integrations/${c.key}`, {
        enabled,
        config: { base_url: baseUrl, api_key: apiKey, notes },
      });
      toast.success(`${c.name}: ${data.status}`);
      setApiKey("");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`connector-${c.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Plug className="h-4 w-4" /></span>
            <p className="font-serif text-lg text-navy">{c.name}</p>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {c.capabilities.map((cap) => (
              <span key={cap} className="rounded-full bg-navy/5 px-2.5 py-0.5 text-[11px] font-medium text-navy">{cap}</span>
            ))}
          </div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[c.status] || STATUS_STYLES.Planned}`} data-testid={`connector-status-${c.key}`}>
          {c.status === "Connected" && <CheckCircle2 className="h-3 w-3" />}{c.status}
        </span>
      </div>
      {c.custom && (
        <button onClick={() => onDelete(c)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-destructive hover:underline" data-testid={`network-delete-${c.key}`}>
          <Trash2 className="h-3.5 w-3.5" /> Remove network
        </button>
      )}

      {c.status === "Connected" && (
        <p className="mt-3 rounded-lg bg-success/5 px-3 py-2 text-[11px] font-medium text-success" data-testid={`connector-scaffold-note-${c.key}`}>
          Configuration saved · live data exchange activates when this connector's client is deployed.
        </p>
      )}

      <button onClick={() => setOpen((v) => !v)} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold" data-testid={`connector-configure-${c.key}`}>
        <Settings2 className="h-4 w-4" /> Configure
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-3 rounded-xl border border-border bg-background p-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-charcoal/70">Endpoint / Base URL</label>
            <input className={inputCls} placeholder="https://api.provider.com" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} data-testid={`connector-baseurl-${c.key}`} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-charcoal/70">API Key {c.configured && <span className="text-success">· stored</span>}</label>
            <input type="password" className={inputCls} placeholder={c.configured ? "•••••••• (leave blank to keep)" : "Enter API key"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid={`connector-apikey-${c.key}`} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-charcoal/70">Notes</label>
            <input className={inputCls} placeholder="Optional internal note" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`connector-notes-${c.key}`} />
          </div>
          <label className="flex items-center gap-2 text-sm text-charcoal">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid={`connector-enable-${c.key}`} /> Enable this connector
          </label>
          <p className="text-xs text-muted-foreground">Saving stores the configuration securely. Live data exchange activates when the connector's API client is deployed in a future phase — no platform rebuild required.</p>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-2 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid={`connector-save-${c.key}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Connector
          </button>
        </div>
      )}
    </div>
  );
};

export default function Integrations() {
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", capabilities: "" });

  const load = () => api.get("/admin/integrations").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load integrations"));
  useEffect(() => { load(); }, []);

  const addNetwork = async () => {
    if (!form.name.trim()) { toast.error("Network name is required"); return; }
    try {
      await api.post("/admin/networks", form);
      toast.success(`Network "${form.name}" added`);
      setAddOpen(false); setForm({ name: "", description: "", capabilities: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add network"); }
  };

  const deleteNetwork = async (c) => {
    if (!window.confirm(`Remove network "${c.name}"?`)) return;
    try { await api.delete(`/admin/networks/${c.key}`); toast.success("Network removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;

  return (
    <div data-testid="integrations-panel">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl text-navy">Integration Hub</h2>
        <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="add-network-btn">
          <Plus className="h-4 w-4" /> Add Network
        </button>
      </div>
      <p className="mb-6 max-w-3xl text-sm text-muted-foreground">
        A modular connector framework keeping the platform as the central intelligence hub. Job boards, recruitment networks, health systems, ATS and CRM connect here. Add unlimited recruitment networks with the "+ Add Network" button — no rebuild required. No live API calls are made until a connector is deployed.
      </p>
      <div className="space-y-8">
        {data.categories.map((cat) => (
          <div key={cat.name}>
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-charcoal/60">{cat.name}</h3>
            <div className="grid gap-4 md:grid-cols-2">
              {cat.connectors.map((c) => <ConnectorCard key={c.key} c={c} onSaved={load} onDelete={deleteNetwork} />)}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy">Add Recruitment Network</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">Add an unlimited number of recruitment networks. No rebuild required — configure API details after adding.</DialogDescription>
          <div className="space-y-4">
            <div><label className="mb-1 block text-sm font-semibold text-charcoal">Network name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Global Health Alliance" data-testid="network-form-name" /></div>
            <div><label className="mb-1 block text-sm font-semibold text-charcoal">Description</label><input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What this network is for" data-testid="network-form-desc" /></div>
            <div><label className="mb-1 block text-sm font-semibold text-charcoal">Capabilities (comma separated)</label><input className={inputCls} value={form.capabilities} onChange={(e) => setForm({ ...form, capabilities: e.target.value })} placeholder="Split Fee, Candidate Exchange" data-testid="network-form-caps" /></div>
            <button onClick={addNetwork} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="network-form-submit"><Plus className="h-4 w-4" /> Add Network</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
